import { useEffect, useRef, useCallback, useState } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

export const useNotifications = (token) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const connect = useCallback(() => {
    if (!token) return;

    try {
      wsRef.current = new WebSocket(`${WS_URL}/ws/notifications/${token}`);

      wsRef.current.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send("ping");
          }
        }, 30000);
        
        wsRef.current.pingInterval = pingInterval;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === "pong") {
            return; // Ignore pong responses
          }

          // Add to notifications list
          setNotifications((prev) => [message, ...prev].slice(0, 50));

          // Show toast based on notification type
          switch (message.type) {
            case "interference_alert":
              toast.error(message.data.message, {
                duration: 10000,
                icon: "🔴",
              });
              // Play alert sound if available
              playAlertSound("critical");
              break;
              
            case "warning_alert":
              toast.warning(message.data.message, {
                duration: 8000,
                icon: "🟡",
              });
              playAlertSound("warning");
              break;
              
            case "system_update":
              toast.info(message.data.message, {
                duration: 5000,
              });
              break;
              
            case "test":
              toast.success(message.data.message, {
                duration: 3000,
              });
              break;
              
            default:
              console.log("Unknown notification type:", message.type);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log("WebSocket closed:", event.code);
        setIsConnected(false);
        
        if (wsRef.current?.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        
        // Only attempt reconnection after 30 seconds and if not auth failure
        // Use longer delay to avoid aggressive reconnection
        if (event.code !== 4001 && event.code !== 1006) { // Don't reconnect if auth failed or connection refused
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Attempting to reconnect...");
            connect();
          }, 30000); // 30 seconds instead of 5
        }
      };

      wsRef.current.onerror = (error) => {
        // Don't spam console with WebSocket errors
        // console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
    }
  }, [token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current?.pingInterval) {
      clearInterval(wsRef.current.pingInterval);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    isConnected,
    notifications,
    clearNotifications,
    reconnect: connect,
  };
};

// Simple audio alert function
const playAlertSound = (type) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    if (type === "critical") {
      oscillator.frequency.value = 880; // High pitch for critical
      oscillator.type = "square";
    } else {
      oscillator.frequency.value = 440; // Lower pitch for warning
      oscillator.type = "sine";
    }

    gainNode.gain.value = 0.1;
    oscillator.start();

    // Short beep
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 200);
  } catch (error) {
    // Audio not supported or blocked
  }
};

export default useNotifications;
